import React, { useState, useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from 'components/ui/dialog'
import { Button } from 'components/ui/button'
import { Input } from 'components/ui/input'
import { Textarea } from 'components/ui/textarea'
import { Label } from 'components/ui/label'
import { Switch } from 'components/ui/switch'
import { Select } from 'components/ui/select'
import { ListTodo } from 'lucide-react'
import {
	createPortfolioTask,
	updatePortfolioTask,
} from 'slices/portfolioTaskCNSlice'
import type { PortfolioTask } from 'types/portfolioTask'

const PRIORITY_OPTIONS = [
	{ value: 'P1 - Urgent', label: 'P1 - Urgent' },
	{ value: 'P2 - High', label: 'P2 - High' },
	{ value: 'P3 - Medium', label: 'P3 - Medium' },
	{ value: 'P4 - Low', label: 'P4 - Low' },
]

const EFFORT_OPTIONS = [
	{ value: 'XS - Trivial', label: 'XS - Trivial' },
	{ value: 'S - Small', label: 'S - Small' },
	{ value: 'M - Medium', label: 'M - Medium' },
	{ value: 'L - Large', label: 'L - Large' },
	{ value: 'XL - Epic', label: 'XL - Epic' },
]

interface CreatePortfolioTaskModalProps {
	open: boolean
	onClose: () => void
	portfolioId: string
	/** When provided, the modal is in edit mode; otherwise it creates a new task. */
	taskToEdit?: PortfolioTask | null
}

const todayInputValue = () => {
	const d = new Date()
	return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

const dateInputValue = (isoOrDate?: string | null) => {
	if (!isoOrDate) return todayInputValue()
	const d = new Date(isoOrDate)
	if (Number.isNaN(d.getTime())) return todayInputValue()
	return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

const refId = (ref: any): string => (typeof ref === 'string' ? ref : ref?._id || '')

const CreatePortfolioTaskModal = ({
	open,
	onClose,
	portfolioId,
	taskToEdit,
}: CreatePortfolioTaskModalProps) => {
	const dispatch = useDispatch()
	const { userInfo } = useSelector((state: any) => state.userLogin)
	const portfolio = useSelector((state: any) => state.portfolios?.entities?.[portfolioId])

	const isEdit = !!taskToEdit

	const [taskName, setTaskName] = useState('')
	const [description, setDescription] = useState('')
	const [taskPriority, setTaskPriority] = useState('P3 - Medium')
	const [taskEffort, setTaskEffort] = useState('M - Medium')
	const [assignedTo, setAssignedTo] = useState(userInfo?._id || '')
	const [dateDue, setDateDue] = useState(todayInputValue())
	// Same contract as CreateTaskCNModal (MYD-534): off keeps the noon-UTC
	// date-only sentinel; on stores the chosen LOCAL date and time.
	const [hasScheduledTime, setHasScheduledTime] = useState(false)
	const [scheduledTime, setScheduledTime] = useState('09:00')
	const [isSubmitting, setIsSubmitting] = useState(false)

	// Reset / hydrate fields whenever the modal opens (or task changes)
	useEffect(() => {
		if (!open) return
		if (taskToEdit) {
			setTaskName(taskToEdit.taskName || '')
			setDescription(taskToEdit.description || '')
			setTaskPriority(taskToEdit.taskPriority || 'P3 - Medium')
			setTaskEffort(taskToEdit.taskEffort || 'M - Medium')
			setAssignedTo(refId(taskToEdit.assignedTo) || userInfo?._id || '')
			if (taskToEdit.hasScheduledTime && taskToEdit.dateDue) {
				// A scheduled time is a real instant: show it in local time, and
				// take the date from the same local reading so they agree.
				const local = new Date(taskToEdit.dateDue)
				const pad = (n: number) => String(n).padStart(2, '0')
				setDateDue(`${local.getFullYear()}-${pad(local.getMonth() + 1)}-${pad(local.getDate())}`)
				setScheduledTime(`${pad(local.getHours())}:${pad(local.getMinutes())}`)
				setHasScheduledTime(true)
			} else {
				setDateDue(dateInputValue(taskToEdit.dateDue || undefined))
				setScheduledTime('09:00')
				setHasScheduledTime(false)
			}
		} else {
			setTaskName('')
			setDescription('')
			setTaskPriority('P3 - Medium')
			setTaskEffort('M - Medium')
			setAssignedTo(userInfo?._id || '')
			setDateDue(todayInputValue())
			setScheduledTime('09:00')
			setHasScheduledTime(false)
		}
	}, [open, taskToEdit, userInfo?._id])

	// Assignable users = portfolio owner + accepted partners + current user (deduped)
	const assignableUsers = useMemo(() => {
		const users: { id: string; name: string }[] = []
		const seen = new Set<string>()
		const push = (id?: string, name?: string) => {
			if (!id || seen.has(id)) return
			seen.add(id)
			users.push({ id, name: name || 'Unknown' })
		}

		if (userInfo?._id) push(userInfo._id, userInfo.name)
		const owner = portfolio?.owner
		if (owner) push(refId(owner), typeof owner === 'object' ? owner.name : undefined)
		;(portfolio?.partners || []).forEach((p: any) => {
			const u = p?.partnerUserId
			if (u) push(refId(u), typeof u === 'object' ? u.name : undefined)
		})

		return users
	}, [portfolio, userInfo])

	const handleClose = () => {
		if (isSubmitting) return
		onClose()
	}

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault()
		const trimmedName = taskName.trim()
		if (!trimmedName) return

		setIsSubmitting(true)
		try {
			const payload = {
				taskName: trimmedName,
				description: description.trim() || undefined,
				taskPriority,
				taskEffort,
				assignedTo: assignedTo || userInfo?._id,
				dateDue: dateDue
					? hasScheduledTime
						? new Date(`${dateDue}T${scheduledTime}:00`).toISOString()
						: `${dateDue}T12:00:00.000Z`
					: undefined,
				hasScheduledTime: Boolean(dateDue) && hasScheduledTime,
			}

			if (isEdit && taskToEdit) {
				await dispatch(
					updatePortfolioTask({
						taskId: taskToEdit._id,
						portfolioId,
						patch: payload,
					}) as any,
				)
			} else {
				await dispatch(
					createPortfolioTask({
						portfolioid: portfolioId,
						...payload,
					}) as any,
				)
			}
			onClose()
		} finally {
			setIsSubmitting(false)
		}
	}

	return (
		<Dialog open={open} onClose={handleClose}>
			<DialogContent>
				<DialogHeader>
					<div className="flex items-center gap-3">
						<div className="w-10 h-10 rounded-lg flex items-center justify-center bg-[#0080fe]/10 text-[#0080fe]">
							<ListTodo className="w-5 h-5" />
						</div>
						<div>
							<DialogTitle>{isEdit ? 'Edit Portfolio Task' : 'New Portfolio Task'}</DialogTitle>
							<DialogDescription>
								{isEdit ? 'Update this portfolio-level task.' : 'Add a task that lives at the portfolio level.'}
							</DialogDescription>
						</div>
					</div>
				</DialogHeader>

				<form onSubmit={handleSubmit} className="p-5 space-y-4">
					<div className="space-y-1.5">
						<Label htmlFor="taskName">
							Task Title <span className="text-red-500">*</span>
						</Label>
						<Input
							id="taskName"
							type="text"
							value={taskName}
							onChange={e => setTaskName(e.target.value)}
							placeholder="e.g., Review Q3 portfolio performance"
							autoFocus
							required
						/>
					</div>

					<div className="grid grid-cols-2 gap-4">
						<div className="space-y-1.5">
							<Label htmlFor="taskPriority">Priority</Label>
							<Select id="taskPriority" value={taskPriority} onChange={e => setTaskPriority(e.target.value)}>
								{PRIORITY_OPTIONS.map(opt => (
									<option key={opt.value} value={opt.value}>
										{opt.label}
									</option>
								))}
							</Select>
						</div>
						<div className="space-y-1.5">
							<Label htmlFor="taskEffort">Effort</Label>
							<Select id="taskEffort" value={taskEffort} onChange={e => setTaskEffort(e.target.value)}>
								{EFFORT_OPTIONS.map(opt => (
									<option key={opt.value} value={opt.value}>
										{opt.label}
									</option>
								))}
							</Select>
						</div>
					</div>

					<div className="grid grid-cols-2 gap-4">
						<div className="space-y-1.5">
							<Label htmlFor="assignedTo">Assigned To</Label>
							<Select id="assignedTo" value={assignedTo} onChange={e => setAssignedTo(e.target.value)}>
								{assignableUsers.map(user => (
									<option key={user.id} value={user.id}>
										{user.name}
										{user.id === userInfo?._id ? ' (me)' : ''}
									</option>
								))}
							</Select>
						</div>
						<div className="space-y-1.5">
							<Label htmlFor="dateDue">Due Date</Label>
							<Input id="dateDue" type="date" value={dateDue} onChange={e => setDateDue(e.target.value)} />
						</div>
					</div>

					{/* Scheduled time toggle + input, as on the other task screens */}
					<div className="grid grid-cols-2 gap-4 items-end">
						<label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
							<Switch
								checked={hasScheduledTime}
								onChange={e => setHasScheduledTime(e.target.checked)}
								disabled={!dateDue}
							/>
							<span>Set specific time</span>
						</label>
						<div className="space-y-1.5">
							<Label htmlFor="scheduledTime">Time</Label>
							<Input
								id="scheduledTime"
								type="time"
								value={scheduledTime}
								onChange={e => setScheduledTime(e.target.value)}
								disabled={!hasScheduledTime}
							/>
						</div>
					</div>

					<div className="space-y-1.5">
						<Label htmlFor="description">
							Description <span className="text-gray-400">(Optional)</span>
						</Label>
						<Textarea
							id="description"
							value={description}
							onChange={e => setDescription(e.target.value)}
							placeholder="Add any additional context or notes..."
							className="min-h-[80px] resize-none"
							rows={3}
						/>
					</div>

					<DialogFooter className="px-0 border-t border-gray-200 pt-4">
						<Button type="button" variant="ghost" onClick={handleClose} disabled={isSubmitting}>
							Cancel
						</Button>
						<Button type="submit" variant="primary" disabled={isSubmitting || !taskName.trim()}>
							{isSubmitting ? (isEdit ? 'Saving…' : 'Creating…') : isEdit ? 'Save Changes' : 'Create Task'}
						</Button>
					</DialogFooter>
				</form>
			</DialogContent>
		</Dialog>
	)
}

export default CreatePortfolioTaskModal
